import { useCallback, useEffect, useRef, useState } from 'react';
import { GetImportStatus } from '../../_services/importRevenue/getImportStatus.service';

// The royalty excel import is queued on a celery worker, so the upload response only
// means "accepted". The real state lives on GET /releases/import-status/{import_id}/.
export const POLL_INTERVAL = 2000;                  // ~2s, do not go below 1s
export const MAX_POLL_DURATION = 30 * 60 * 1000;    // stop nagging the API after 30 min
const ACTIVE_IMPORT_KEY = 'activeRevenueImport';

export const isTerminalStatus = (status) => status === 'success' || status === 'failed';

// the API is the source of truth for what went wrong, e.g. {"detail":"Invalid token."}
export const readApiError = (response) => {
  const data = response && response.data;
  if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data.trim();
  if (data && typeof data === 'object') {
    const direct = data.detail || data.message || data.error;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();
    // nested {message: {message: '...'}} and DRF field errors {field: ['...']}
    if (direct && typeof direct === 'object') {
      const nested = readApiError({ data: direct });
      if (nested) return nested;
    }
    const fieldErrors = Object.values(data)
      .flat()
      .filter((v) => typeof v === 'string' && v.trim());
    if (fieldErrors.length) return fieldErrors.join(' ');
  }
  return '';
};

// persisted so the progress can be resumed when the user navigates away and comes back
export const readActiveImport = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_IMPORT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeActiveImport = (value) => {
  if (value) {
    localStorage.setItem(ACTIVE_IMPORT_KEY, JSON.stringify(value));
  } else {
    localStorage.removeItem(ACTIVE_IMPORT_KEY);
  }
};

export default function useImportStatus({ onSuccess, onFailed, onStalled, onError } = {}) {
  const [importId, setImportId] = useState(null);
  const [fileName, setFileName] = useState('');
  const [status, setStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollMessage, setPollMessage] = useState('');
  const [pollError, setPollError] = useState('');

  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const startedAtRef = useRef(0);

  // keep the callbacks fresh without restarting the poll loop
  const handlersRef = useRef({});
  handlersRef.current = { onSuccess, onFailed, onStalled, onError };

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    cancelledRef.current = true;
    clearTimer();
    setIsPolling(false);
  }, [clearTimer]);

  // a request that failed for good: stop the loop, surface it, never retry
  const failPolling = useCallback((message, httpStatus) => {
    stopPolling();
    writeActiveImport(null);
    setPollError(message);
    if (handlersRef.current.onError) handlersRef.current.onError({ status: httpStatus, message });
  }, [stopPolling]);

  const poll = useCallback(async (id) => {
    const response = await GetImportStatus(id);
    if (cancelledRef.current) return;

    // any failed response ends the loop, the API message is what the user is told
    if (!response || response.status !== 200) {
      const httpStatus = response && response.status;
      failPolling(
        readApiError(response) || 'Could not reach the server. Please try again.',
        httpStatus,
      );
      return;
    }

    const data = response.data || {};
    setStatus(data);

    // "done" is driven off the status, never off progress_percent
    if (data.processing_status === 'success') {
      stopPolling();
      writeActiveImport(null);
      if (handlersRef.current.onSuccess) handlersRef.current.onSuccess(data);
      return;
    }
    // failed can still mean the valid rows were committed, see rows_created
    if (data.processing_status === 'failed') {
      stopPolling();
      writeActiveImport(null);
      if (handlersRef.current.onFailed) handlersRef.current.onFailed(data);
      return;
    }

    if (Date.now() - startedAtRef.current > MAX_POLL_DURATION) {
      stopPolling();
      setPollMessage('Still processing. You can close this and check the royalty list later.');
      if (handlersRef.current.onStalled) handlersRef.current.onStalled(id);
      return;
    }

    timerRef.current = setTimeout(() => poll(id), POLL_INTERVAL);
  }, [stopPolling, failPolling]);

  const start = useCallback((id, name = '', startedAt = Date.now()) => {
    clearTimer();
    cancelledRef.current = false;
    startedAtRef.current = startedAt;
    setImportId(id);
    setFileName(name);
    setStatus({ processing_status: 'pending', progress_percent: 0 });
    setPollMessage('');
    setPollError('');
    setIsPolling(true);
    writeActiveImport({ importId: id, fileName: name, startedAt });
    poll(id);
  }, [clearTimer, poll]);

  const reset = useCallback(() => {
    stopPolling();
    writeActiveImport(null);
    setImportId(null);
    setFileName('');
    setStatus(null);
    setPollMessage('');
    setPollError('');
  }, [stopPolling]);

  // resume an import that was still running when the page/route was left
  useEffect(() => {
    const active = readActiveImport();
    if (active && active.importId) {
      start(active.importId, active.fileName, active.startedAt || Date.now());
    }
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processingStatus = status ? status.processing_status : null;

  return {
    importId,
    fileName,
    status,
    processingStatus,
    isPolling,
    pollMessage,
    pollError,
    // there is something worth showing: either running, or finished and not dismissed yet
    isActive: isPolling || isTerminalStatus(processingStatus) || !!pollMessage || !!pollError,
    start,
    reset,
    stopPolling,
  };
}
